import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MailCheck, Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, setUser, refresh } = useAuth();
  const [mode, setMode] = useState(token ? "verifying" : "pending");
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data.user) setUser(data.user);
        else await refresh();
        setMode("success");
      } catch {
        setMode("error");
      }
    })();
  }, [token, setUser, refresh]);

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email de vérification renvoyé !");
    } catch (err) {
      const s = err.response?.status;
      if (s === 401) { toast.error("Connectez-vous pour renvoyer l'email."); navigate("/login"); }
      else toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="studio-bg grain min-h-screen grid place-items-center px-5 py-10 relative">
      <div className="relative z-10 w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3ECF8E] to-emerald-600 grid place-items-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-xl font-extrabold">Vira<span className="text-[#3ECF8E]">lyne</span></span>
        </Link>

        <div className="glass rounded-3xl p-8 text-center" data-testid="verify-email-card">
          {mode === "verifying" && (
            <>
              <Loader2 className="w-14 h-14 text-[#3ECF8E] animate-spin mx-auto" />
              <h1 className="font-display text-2xl font-bold mt-5">Vérification en cours…</h1>
            </>
          )}

          {mode === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" data-testid="verify-success" />
              <h1 className="font-display text-2xl font-bold mt-5">Email confirmé !</h1>
              <p className="text-slate-400 mt-2">Votre compte est actif. Choisissez une formule pour commencer.</p>
              <Button onClick={() => navigate("/pricing")} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="verify-go-pricing">
                Voir les formules
              </Button>
            </>
          )}

          {mode === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto" data-testid="verify-error" />
              <h1 className="font-display text-2xl font-bold mt-5">Lien invalide ou expiré</h1>
              <p className="text-slate-400 mt-2">Demandez un nouveau lien de vérification.</p>
              <Button onClick={resend} disabled={resending} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="verify-resend-error">
                {resending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Renvoyer l'email"}
              </Button>
            </>
          )}

          {mode === "pending" && (
            <>
              <MailCheck className="w-16 h-16 text-[#3ECF8E] mx-auto" />
              <h1 className="font-display text-2xl font-bold mt-5">Vérifiez votre email</h1>
              <p className="text-slate-400 mt-2">
                Nous avons envoyé un lien de confirmation{user?.email ? <> à <span className="text-slate-200">{user.email}</span></> : ""}.
                Cliquez dessus pour activer votre compte.
              </p>
              <p className="text-xs text-slate-500 mt-3">Pensez à vérifier vos spams.</p>
              <Button onClick={resend} disabled={resending} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold w-full" data-testid="resend-verification-button">
                {resending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Renvoyer l'email"}
              </Button>
              <button onClick={() => navigate("/login")} className="text-sm text-slate-400 hover:text-white mt-4 transition-colors" data-testid="back-to-login">
                Retour à la connexion
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
